import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3, TrendingUp, Brain, BookOpen, Plug, PieChart,
  CheckCircle, ChevronDown
} from 'lucide-react';
import { ImageLightbox } from '@/components/ui/image-lightbox';

// PT screenshots (full)
import ptExecutive from '@/assets/screenshots/pt/executive-overview.png';
import ptSalesAnalytics from '@/assets/screenshots/pt/sales-analytics.png';
import ptBurndown from '@/assets/screenshots/pt/sales-burndown.png';
import ptAiAdvisor from '@/assets/screenshots/pt/ai-advisor.png';
import ptPlaybooks from '@/assets/screenshots/pt/playbooks.png';
import ptIntegrations from '@/assets/screenshots/pt/integrations.png';

// EN screenshots (full, fallback to PT when not available)
import enExecutive from '@/assets/screenshots/en/executive-overview.png';
import enBurndown from '@/assets/screenshots/en/sales-burndown.png';

// PT thumbnails
import ptExecutiveThumb from '@/assets/screenshots/pt/thumb/executive-overview.jpg';
import ptSalesAnalyticsThumb from '@/assets/screenshots/pt/thumb/sales-analytics.jpg';
import ptBurndownThumb from '@/assets/screenshots/pt/thumb/sales-burndown.jpg';
import ptAiAdvisorThumb from '@/assets/screenshots/pt/thumb/ai-advisor.jpg';
import ptPlaybooksThumb from '@/assets/screenshots/pt/thumb/playbooks.jpg';
import ptIntegrationsThumb from '@/assets/screenshots/pt/thumb/integrations.jpg';

// EN thumbnails
import enExecutiveThumb from '@/assets/screenshots/en/thumb/executive-overview.jpg';
import enBurndownThumb from '@/assets/screenshots/en/thumb/sales-burndown.jpg';

const fullByLang = {
  pt: [ptExecutive, ptSalesAnalytics, ptBurndown, ptAiAdvisor, ptPlaybooks, ptIntegrations],
  en: [enExecutive, ptSalesAnalytics, enBurndown, ptAiAdvisor, ptPlaybooks, ptIntegrations],
};

const thumbByLang = {
  pt: [ptExecutiveThumb, ptSalesAnalyticsThumb, ptBurndownThumb, ptAiAdvisorThumb, ptPlaybooksThumb, ptIntegrationsThumb],
  en: [enExecutiveThumb, ptSalesAnalyticsThumb, enBurndownThumb, ptAiAdvisorThumb, ptPlaybooksThumb, ptIntegrationsThumb],
};

const tabIcons = [BarChart3, PieChart, TrendingUp, Brain, BookOpen, Plug];

export function FeaturesShowcase() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const isPt = i18n.language?.startsWith('pt');
  const thumbs = isPt ? thumbByLang.pt : thumbByLang.en;
  const fulls = isPt ? fullByLang.pt : fullByLang.en;

  const tabs = [
    {
      label: t('landing.features.tab1'),
      bullets: [t('landing.features.tab1b1'), t('landing.features.tab1b2'), t('landing.features.tab1b3'), t('landing.features.tab1b4')],
    },
    {
      label: t('landing.features.tab2'),
      bullets: [t('landing.features.tab2b1'), t('landing.features.tab2b2'), t('landing.features.tab2b3'), t('landing.features.tab2b4')],
    },
    {
      label: t('landing.features.tab3'),
      bullets: [t('landing.features.tab3b1'), t('landing.features.tab3b2'), t('landing.features.tab3b3'), t('landing.features.tab3b4')],
    },
    {
      label: t('landing.features.tab4'),
      bullets: [t('landing.features.tab4b1'), t('landing.features.tab4b2'), t('landing.features.tab4b3'), t('landing.features.tab4b4')],
    },
    {
      label: t('landing.features.tab5'),
      bullets: [t('landing.features.tab5b1'), t('landing.features.tab5b2'), t('landing.features.tab5b3'), t('landing.features.tab5b4')],
    },
    {
      label: t('landing.features.tab6'),
      bullets: [t('landing.features.tab6b1')],
      extra: (
        <div className="mt-4">
          <p className="text-sm font-semibold text-[#4F6CFF]">{t('landing.features.tab6Logos')}</p>
          <p className="text-xs text-[#1A1F3C]/40 mt-1">{t('landing.features.tab6More')}</p>
        </div>
      ),
    },
  ];

  const current = tabs[activeTab];

  return (
    <section className="py-20 bg-white">
      <div className="container">
        <h2 className="mb-12 text-center text-3xl font-bold tracking-tight text-[#1A1F3C] md:text-4xl">
          {t('landing.features.headline')}
        </h2>

        {/* Desktop tabs */}
        <div className="hidden md:flex items-center justify-center gap-1 mb-12 bg-[#F4F5FF] rounded-xl p-1.5 max-w-4xl mx-auto">
          {tabs.map((tab, i) => {
            const Icon = tabIcons[i];
            return (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === i
                    ? 'bg-[#4F6CFF] text-white shadow-md'
                    : 'text-[#1A1F3C]/60 hover:text-[#1A1F3C] hover:bg-white/60'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Mobile accordion */}
        <div className="md:hidden space-y-2 mb-8">
          {tabs.map((tab, i) => {
            const Icon = tabIcons[i];
            const isOpen = activeTab === i;
            return (
              <div key={i} className="border border-[#1A1F3C]/10 rounded-lg overflow-hidden">
                <button
                  onClick={() => setActiveTab(i)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${
                    isOpen ? 'bg-[#4F6CFF] text-white' : 'bg-[#F4F5FF] text-[#1A1F3C]'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="p-4 bg-white">
                    <ul className="space-y-3">
                      {tab.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#00C896] mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-[#1A1F3C]/70">{b}</span>
                        </li>
                      ))}
                    </ul>
                    {tab.extra}
                    <div className="mt-4 rounded-lg overflow-hidden border border-[#1A1F3C]/10">
                      <ImageLightbox
                        src={thumbs[i]}
                        fullSrc={fulls[i]}
                        alt={tab.label}
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop tab content */}
        <div className="hidden md:grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
          <div>
            <ul className="space-y-4">
              {current.bullets.map((b, j) => (
                <li key={j} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00C896] mt-0.5 flex-shrink-0" />
                  <span className="text-[#1A1F3C]/80 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
            {current.extra}
          </div>

          <div className="rounded-xl border border-[#1A1F3C]/10 bg-[#F4F5FF] p-2 shadow-lg">
            <div className="rounded-lg overflow-hidden bg-[#1A1F3C]">
              <div className="flex items-center gap-1.5 px-3 py-2 bg-[#1A1F3C] border-b border-white/10">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400/50" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/50" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400/50" />
              </div>
              <div className="aspect-[16/10] overflow-hidden">
                <ImageLightbox
                  src={thumbs[activeTab]}
                  fullSrc={fulls[activeTab]}
                  alt={current.label}
                  className="w-full h-full object-cover object-top"
                  eager={activeTab === 0}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
